import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import BigNumber from 'bignumber.js';
import { MIN_VALUES } from '../../../constants/minValues';
import RustCalculatorApi from '../../../api/rustApi';
import UserSettingsApi from '../../../api/userSettingsApi';
import type { CalculatorRequest, RiskUnit } from '../../../types/calculator';
import { computeMultiExits, defaultTpExits } from '../../../utils/multiExits';

// Use the created API client for Rust instead of direct fetch requests
const CALCULATION_DELAY = 100;
const LOCALSTORAGE_DEBOUNCE = 500;
const API_SYNC_DEBOUNCE = 2000; // 2 seconds for API sync
const LOCALSTORAGE_KEY = 'calculatorSettings';

BigNumber.config({
    EXPONENTIAL_AT: 1e+9,
    DECIMAL_PLACES: 8,
    ROUNDING_MODE: BigNumber.ROUND_DOWN,
    FORMAT: {
        decimalSeparator: '.',
        groupSeparator: '',
        groupSize: 0,
        secondaryGroupSize: 0,
        fractionGroupSeparator: '',
        fractionGroupSize: 0
    }
});

const defaultValues = {
    risk: new BigNumber(10),
    riskUnit: 'fixed',
    accountBalance: new BigNumber(25000),
    nominalRR: new BigNumber(2),
    maxLeverage: new BigNumber(125),
    entryFee: new BigNumber(0.05),
    exitFee: new BigNumber(0.05),
    safetyBufferR: new BigNumber(1),
    entryPrice: new BigNumber(32.05),
    stopLoss: new BigNumber(31.65),
    // Multiple take-profits (partial exits). When disabled the calculator
    // behaves exactly as before (single TP from nominalRR).
    multiTpEnabled: false,
    tpCount: 3,
    tpMode: 'percent',
    tpExits: defaultTpExits(),
};

const defaultMetrics = {
    positionSize: '--',
    quantity: '--',
    leverage: '--',
    margin: '--',
    effectiveTake: '--',
    nominalTake: '--',
    effectiveStop: '--',
    nominalStop: '--',
    effectiveRR: '--',
    friction: '--',
    nominalRR: '--',
    optimizedRR: '--',
    priceLevels: {
        liquidationPrice: '--',
        breakEven: '--',
        takeProfit: '--',
        optimizedTakeProfit: '--',
        stopLoss: '--'
    },
    tradingFees: {
        entryFee: '--',
        tpFee: '--',
        slFee: '--'
    },
    totalFees: {
        takeProfitFees: '--',
        stopLossFees: '--'
    },
    multiExit: null,
};

// localStorage utility functions
const serializeValues = (values: typeof defaultValues) => {
    const serialized: Record<string, any> = {};
    
    Object.entries(values).forEach(([key, value]) => {
        if (key === 'tpExits' && Array.isArray(value)) {
            serialized[key] = value.map((e: any) => ({
                alloc: e.alloc instanceof BigNumber ? e.alloc.toString() : e.alloc,
                rMultiple: e.rMultiple instanceof BigNumber ? e.rMultiple.toString() : e.rMultiple,
            }));
        } else if (value instanceof BigNumber) {
            serialized[key] = value.toString();
        } else {
            serialized[key] = value;
        }
    });

    return JSON.stringify(serialized);
};

const deserializeValues = (serializedData: string) => {
    try {
        const parsed = JSON.parse(serializedData);
        const deserialized: any = { ...defaultValues };
        
        Object.entries(parsed).forEach(([key, value]) => {
            // Skip timestamp and other non-calculator fields
            if (key === 'updatedAt' || !(key in defaultValues)) {
                return;
            }
            
            if (key === 'riskUnit' || key === 'tpMode') {
                deserialized[key] = value as any;
            } else if (key === 'tpExits') {
                deserialized[key] = (Array.isArray(value)
                    ? value.map((e: any) => ({
                        alloc: new BigNumber(e.alloc),
                        rMultiple: new BigNumber(e.rMultiple),
                    }))
                    : defaultTpExits()) as any;
            } else if (key === 'multiTpEnabled' || key === 'tpCount') {
                deserialized[key] = value as any;
            } else if (typeof value === 'string') {
                // Convert string back to BigNumber
                try {
                    deserialized[key] = new BigNumber(value);
                } catch (e) {
                    console.warn(`Failed to parse BigNumber for ${key}:`, e);
                    // Keep default value
                }
            } else {
                deserialized[key] = value;
            }
        });
        
        return deserialized;
    } catch (e) {
        console.warn('Failed to deserialize calculator values from localStorage:', e);
        return defaultValues;
    }
};

const loadFromLocalStorage = (): typeof defaultValues => {
    try {
        const stored = localStorage.getItem(LOCALSTORAGE_KEY);
        if (stored) {
            return deserializeValues(stored);
        }
    } catch (e) {
        console.warn('Failed to load from localStorage:', e);
    }
    return defaultValues;
};

const saveToLocalStorage = (values: typeof defaultValues) => {
    try {
        const serialized = serializeValues(values);
        const settingsWithTimestamp = {
            ...JSON.parse(serialized),
            updatedAt: new Date().toISOString()
        };
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(settingsWithTimestamp));
    } catch (e) {
        console.warn('Failed to save to localStorage:', e);
    }
};

export const useCalculator = () => {
    const [values, setValues] = useState(() => loadFromLocalStorage());
    const [metrics, setMetrics] = useState(defaultMetrics);
    const [isCalculating, setIsCalculating] = useState(false);
    const [extremePriceField, setExtremePriceField] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [apiSyncStatus, setApiSyncStatus] = useState('idle'); // 'idle', 'loading', 'syncing', 'error'
    const lastRequestId = useRef(0);
    const settingsLoadedFromApi = useRef(false);
    const initialLoadComplete = useRef(false);

    // Debounced function to save values to localStorage
    const debouncedSaveToLocalStorage = useMemo(
        () => debounce((valuesToSave: typeof defaultValues) => {
            saveToLocalStorage(valuesToSave);
        }, LOCALSTORAGE_DEBOUNCE),
        []
    );

    // Debounced function to sync values to API
    const debouncedSyncToApi = useMemo(
        () => debounce(async (valuesToSync: typeof defaultValues) => {
            // Only sync if user is authenticated
            const canSync = await UserSettingsApi.canSyncSettings();
            if (!canSync) {
                console.log('[Calculator] User not authenticated, skipping API sync');
                return;
            }

            try {
                console.log('[Calculator] Syncing settings to API...');
                setApiSyncStatus('syncing');
                
                // Convert BigNumber values to strings for API
                const serializedSettings = serializeValues(valuesToSync);
                const success = await UserSettingsApi.updateUserSettings(JSON.parse(serializedSettings));
                
                if (success) {
                    console.log('[Calculator] Settings successfully synced to API');
                    setApiSyncStatus('idle');
                } else {
                    console.warn('[Calculator] Failed to sync settings to API');
                    setApiSyncStatus('error');
                    // Reset status after a delay
                    setTimeout(() => setApiSyncStatus('idle'), 5000);
                }
            } catch (error) {
                console.error('[Calculator] Error syncing settings to API:', error);
                setApiSyncStatus('error');
                // Reset status after a delay
                setTimeout(() => setApiSyncStatus('idle'), 5000);
            }
        }, API_SYNC_DEBOUNCE),
        []
    );

    // Load settings from API on mount (if authenticated)
    const loadSettingsFromApi = useCallback(async () => {
        if (settingsLoadedFromApi.current) return;
        
        try {
            console.log('[Calculator] Checking for settings in API...');
            setApiSyncStatus('loading');
            
            const apiSettings = await UserSettingsApi.getUserSettings();
            
            if (apiSettings && apiSettings.settings) {
                console.log('[Calculator] Found settings in API, checking if newer than localStorage');
                
                // Check if API settings are newer than current localStorage
                const currentLocalStorage = localStorage.getItem(LOCALSTORAGE_KEY);
                let shouldUpdate = true;
                
                if (currentLocalStorage) {
                    try {
                        const currentParsed = JSON.parse(currentLocalStorage);
                        const apiTimestamp = apiSettings.updatedAt ? new Date(apiSettings.updatedAt).getTime() : 0;
                        const localTimestamp = currentParsed.updatedAt ? new Date(currentParsed.updatedAt).getTime() : 0;
                        
                        // Only update if API settings are newer (or no local timestamp)
                        shouldUpdate = !localTimestamp || apiTimestamp > localTimestamp;
                        console.log('[Calculator] API timestamp:', apiTimestamp, 'Local timestamp:', localTimestamp, 'Should update:', shouldUpdate);
                    } catch (e) {
                        console.warn('[Calculator] Error comparing timestamps:', e);
                        // Default to updating if we can't compare
                        shouldUpdate = true;
                    }
                }
                
                if (shouldUpdate) {
                    console.log('[Calculator] Updating calculator with API settings');
                    const deserializedSettings = deserializeValues(JSON.stringify(apiSettings.settings));
                    setValues(deserializedSettings);
                    
                    // Save to localStorage with timestamp
                    const settingsWithTimestamp = {
                        ...apiSettings.settings,
                        updatedAt: apiSettings.updatedAt || new Date().toISOString()
                    };
                    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(settingsWithTimestamp));
                } else {
                    console.log('[Calculator] Local settings are newer, keeping current values');
                }
            } else {
                console.log('[Calculator] No settings found in API, using localStorage');
            }
            
            setApiSyncStatus('idle');
        } catch (error) {
            console.error('[Calculator] Error loading settings from API:', error);
            setApiSyncStatus('error');
            // Reset status after a delay
            setTimeout(() => setApiSyncStatus('idle'), 5000);
        } finally {
            settingsLoadedFromApi.current = true;
            initialLoadComplete.current = true;
        }
    }, []);

    // Load API settings on mount
    useEffect(() => {
        // Small delay to allow auth to be ready
        const timer = setTimeout(() => {
            loadSettingsFromApi();
        }, 100);
        
        return () => clearTimeout(timer);
    }, [loadSettingsFromApi]);

    // Save values to localStorage and sync to API whenever they change
    useEffect(() => {
        // Always save to localStorage immediately (with debounce)
        debouncedSaveToLocalStorage(values);
        
        // Only sync to API after initial load is complete to avoid syncing default values
        if (initialLoadComplete.current) {
            debouncedSyncToApi(values);
        }
        
        return () => {
            debouncedSaveToLocalStorage.cancel();
            debouncedSyncToApi.cancel();
        };
    }, [values, debouncedSaveToLocalStorage, debouncedSyncToApi]);

    const validateField = useCallback((field, value) => {
        if (!value || value.isLessThanOrEqualTo(0)) {
            return true;
        }
        return false;
    }, []);

    // Check if a value is below the min value and should show an error
    const checkMinValueErrors = useCallback((currentValues) => {
        const errors = {};

        if (currentValues.risk && currentValues.risk.isLessThan(MIN_VALUES.risk)) {
            errors.risk = true;
        }
        if (currentValues.nominalRR && currentValues.nominalRR.isLessThan(MIN_VALUES.nominalRR)) {
            errors.nominalRR = true;
        }
        if (currentValues.accountBalance && currentValues.accountBalance.isLessThan(MIN_VALUES.accountBalance)) {
            errors.accountBalance = true;
        }
        if (currentValues.maxLeverage && currentValues.maxLeverage.isLessThan(MIN_VALUES.maxLeverage)) {
            errors.maxLeverage = true;
        }
        if (currentValues.entryFee && currentValues.entryFee.isLessThan(MIN_VALUES.entryFee)) {
            errors.entryFee = true;
        }
        if (currentValues.exitFee && currentValues.exitFee.isLessThan(MIN_VALUES.exitFee)) {
            errors.exitFee = true;
        }
        if (currentValues.safetyBufferR && currentValues.safetyBufferR.isLessThan(MIN_VALUES.safetyBufferR)) {
            errors.safetyBufferR = true;
        }

        return errors;
    }, []);

    // Update validation errors when values change
    useEffect(() => {
        const newErrors = checkMinValueErrors(values);
        setValidationErrors(newErrors);
    }, [values, checkMinValueErrors]);

    const updateValues = useCallback((newValues) => {
        // Handle functional updates properly
        if (typeof newValues === 'function') {
            setValues(prevValues => {
                const actualNewValues = newValues(prevValues);

                // Create a copy for sanitization
                const sanitizedValues = { ...actualNewValues };

                // Apply min value corrections
                let hasChanges = false;
                if (sanitizedValues.nominalRR && sanitizedValues.nominalRR.isLessThan(MIN_VALUES.nominalRR)) {
                    sanitizedValues.nominalRR = MIN_VALUES.nominalRR;
                    hasChanges = true;
                }
                if (sanitizedValues.risk && sanitizedValues.risk.isLessThan(MIN_VALUES.risk)) {
                    sanitizedValues.risk = MIN_VALUES.risk;
                    hasChanges = true;
                }
                if (sanitizedValues.accountBalance && sanitizedValues.accountBalance.isLessThan(MIN_VALUES.accountBalance)) {
                    sanitizedValues.accountBalance = MIN_VALUES.accountBalance;
                    hasChanges = true;
                }
                if (sanitizedValues.maxLeverage && sanitizedValues.maxLeverage.isLessThan(MIN_VALUES.maxLeverage)) {
                    sanitizedValues.maxLeverage = MIN_VALUES.maxLeverage;
                    hasChanges = true;
                }
                if (sanitizedValues.entryFee && sanitizedValues.entryFee.isLessThan(MIN_VALUES.entryFee)) {
                    sanitizedValues.entryFee = MIN_VALUES.entryFee;
                    hasChanges = true;
                }
                if (sanitizedValues.exitFee && sanitizedValues.exitFee.isLessThan(MIN_VALUES.exitFee)) {
                    sanitizedValues.exitFee = MIN_VALUES.exitFee;
                    hasChanges = true;
                }
                if (sanitizedValues.safetyBufferR && sanitizedValues.safetyBufferR.isLessThan(MIN_VALUES.safetyBufferR)) {
                    sanitizedValues.safetyBufferR = MIN_VALUES.safetyBufferR;
                    hasChanges = true;
                }

                // If changes were made because of min value corrections, return the sanitized values
                return hasChanges ? sanitizedValues : actualNewValues;
            });
            return true;
        }

        if (!newValues) return false;

        const errors = {};
        Object.entries(newValues).forEach(([field, value]) => {
            if (value instanceof BigNumber && validateField(field, value)) {
                errors[field] = true;
            }
        });

        const changedField = Object.keys(newValues).find(key => {
            const isOldBigNumber = values[key] && values[key].constructor && values[key].constructor.name === 'BigNumber';
            const isNewBigNumber = newValues[key] && newValues[key].constructor && newValues[key].constructor.name === 'BigNumber';

            if (isOldBigNumber && isNewBigNumber) {
                return !values[key].isEqualTo(newValues[key]);
            }
            return values[key] !== newValues[key];
        });
        console.log('Changed field:', changedField);

        if (newValues.entryPrice && newValues.stopLoss) {
            console.log('Checking price range:', {
                entryPrice: newValues.entryPrice.toString(),
                stopLoss: newValues.stopLoss.toString(),
                changedField
            });
            isValidPriceRange(newValues.entryPrice, newValues.stopLoss, changedField);
        }

        setValues(newValues);
        return Object.keys(errors).length === 0;
    }, [validateField, values]);

    const isValidPriceRange = (entryPrice, stopLoss, sourceField = null) => {
        if (!entryPrice || !stopLoss || entryPrice.isZero() || stopLoss.isZero()) {
            return false;
        }

        const bigger = BigNumber.max(entryPrice, stopLoss);
        const smaller = BigNumber.min(entryPrice, stopLoss);
        const priceRatio = bigger.dividedBy(smaller);

        console.log('Price analysis:', {
            entryPrice: entryPrice.toString(),
            stopLoss: stopLoss.toString(),
            ratio: priceRatio.toString(),
            sourceField
        });

        if (priceRatio.gt(20)) {
            console.log('Extreme ratio detected:', priceRatio.toString());

            if (sourceField) {
                setExtremePriceField(sourceField === 'entryPrice' ? 'stopLoss' : 'entryPrice');
            } else {
                setExtremePriceField(entryPrice.gt(stopLoss) ? 'entryPrice' : 'stopLoss');
            }
            return false;
        }

        const priceDiff = entryPrice.minus(stopLoss).abs();
        const relativeDiff = priceDiff.dividedBy(BigNumber.max(entryPrice, stopLoss));
        if (relativeDiff.lt(0.0001)) {
            if (sourceField) {
                setExtremePriceField(sourceField === 'entryPrice' ? 'stopLoss' : 'entryPrice');
            } else {
                setExtremePriceField(entryPrice.gt(stopLoss) ? 'entryPrice' : 'stopLoss');
            }
            return false;
        }

        console.log('Price range is valid');
        setExtremePriceField(null);
        return true;
    };

    const calculateMetrics = useCallback(async () => {
        const currentRequestId = lastRequestId.current + 1;
        lastRequestId.current = currentRequestId;

        if (!isValidPriceRange(values.entryPrice, values.stopLoss)) {
            setMetrics(defaultMetrics);
            return;
        }

        try {
            setIsCalculating(true);

            // Create canonical API request structure with string values for precision
            const requestBody: CalculatorRequest = {
                entryPrice: values.entryPrice.toString(),
                stopLoss: values.stopLoss.toString(),
                risk: values.risk.toString(),
                accountBalance: values.accountBalance.toString(),
                riskUnit: values.riskUnit as RiskUnit,
                nominalRR: values.nominalRR.toString(),
                fees: {
                    entry: values.entryFee.toString(),
                    exit: values.exitFee.toString()
                },
                margin: {
                    safety: values.safetyBufferR.toString(),
                    // Ensure maxLeverage is always present and has a correct value
                    maxLeverage: values.maxLeverage ? values.maxLeverage.toString() : "125"
                }
            };
            
            // SAFETY: Normalize riskUnit to valid values for API compatibility
            let normalizedRiskUnit = values.riskUnit;
            if (values.riskUnit === '%' || values.riskUnit === 'percentage') {
                normalizedRiskUnit = 'percent';
            } else if (values.riskUnit === '$' || values.riskUnit === 'dollar' || values.riskUnit === 'dollars') {
                normalizedRiskUnit = 'fixed';
            } else if (!['percent', 'fixed'].includes(values.riskUnit)) {
                // Default to 'fixed' if invalid
                normalizedRiskUnit = 'fixed';
            }
            
            // Update the requestBody with normalized value
            requestBody.riskUnit = normalizedRiskUnit;

            // Use API client instead of direct fetch
            const data = await RustCalculatorApi.calculate(requestBody);

            if (currentRequestId !== lastRequestId.current) return;

            console.log('Raw data from server:', data);
            
            // Check that data contains all required fields
            if (!data.priceLevels || !data.tradingFees || !data.totalFees) {
                console.error('Invalid response structure:', data);
                throw new Error('Invalid response structure from server');
            }
            
            const formattedData = {
                ...data,
                positionSize: new BigNumber(data.positionSize),
                quantity: new BigNumber(data.quantity),
                leverage: new BigNumber(data.leverage),
                margin: new BigNumber(data.margin),
                effectiveTake: new BigNumber(data.effectiveTake),
                nominalTake: new BigNumber(data.nominalTake),
                effectiveStop: new BigNumber(data.effectiveStop),
                nominalStop: new BigNumber(data.nominalStop),
                effectiveRR: new BigNumber(data.effectiveRR),
                friction: new BigNumber(data.friction),
                nominalRR: new BigNumber(data.nominalRR),
                optimizedRR: new BigNumber(data.optimizedRR),
                priceLevels: {
                    liquidationPrice: data.priceLevels.liquidationPrice,
                    breakEven: data.priceLevels.breakEven,
                    takeProfit: data.priceLevels.takeProfit,
                    optimizedTakeProfit: data.priceLevels.optimizedTakeProfit,
                    stopLoss: data.priceLevels.stopLoss
                },
                tradingFees: {
                    entryFee: data.tradingFees.entryFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.entryFee),
                    tpFee: data.tradingFees.tpFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.tpFee),
                    slFee: data.tradingFees.slFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.slFee),
                },
                totalFees: {
                    takeProfitFees: data.totalFees.takeProfitFees === '--' ? new BigNumber(0) : new BigNumber(data.totalFees.takeProfitFees),
                    stopLossFees: data.totalFees.stopLossFees === '--' ? new BigNumber(0) : new BigNumber(data.totalFees.stopLossFees),
                }
            };
            
            // priceDecimals intentionally omitted — computeMultiExits' own default
            // (8dp) preserves full precision. Deriving it from breakEven's incidental
            // decimal count used to silently truncate target prices below what's
            // needed to reproduce the stated R-multiple.
            const multiExit = values.multiTpEnabled
                ? computeMultiExits({
                    entryPrice: values.entryPrice,
                    stopDistance: values.entryPrice.minus(values.stopLoss).abs(),
                    tpPathFee: formattedData.totalFees.takeProfitFees,
                    exits: values.tpExits,
                    mode: values.tpMode,
                    quantity: formattedData.quantity,
                    riskAmount: formattedData.effectiveStop,
                    breakEven: formattedData.priceLevels.breakEven,
                    optimizedTakeProfit: formattedData.priceLevels.optimizedTakeProfit,
                    nominalRR: formattedData.nominalRR,
                    side: values.entryPrice.gt(values.stopLoss) ? 'long' : 'short',
                })
                : null;

            setMetrics({ ...formattedData, multiExit });
        } catch (err) {
            console.error('Calculation error:', err);
            setMetrics(defaultMetrics);
        } finally {
            if (currentRequestId === lastRequestId.current) {
                setIsCalculating(false);
            }
        }
    }, [values]);

    const debouncedCalculateMetrics = useMemo(
        () => debounce(calculateMetrics, CALCULATION_DELAY),
        [calculateMetrics]
    );

    useEffect(() => {
        debouncedCalculateMetrics();
        return () => debouncedCalculateMetrics.cancel();
    }, [debouncedCalculateMetrics]);

    return {
        values,
        setValues: updateValues,
        metrics,
        calculateMetrics: debouncedCalculateMetrics,
        isCalculating,
        validationErrors,
        extremePriceField,
        apiSyncStatus,
        loadSettingsFromApi,
    };
};